/**
 * RfqResultsBanner — the standalone "Request RFQ" entry point, shown once in a
 * banner row directly above the rate-card list (mirrors the results-summary
 * header CTA in the workflow mockup).
 *
 * Two distinct entry points, by design:
 *   - This banner: the whole-lane case — no competitive rate for the target
 *     ETD, or a door-to-door lane the marketplace can't fully quote. One click
 *     opens the RFQ creator page for the whole lane.
 *   - The in-card button (ViewDetailsFooter): the per-card case — a *specific*
 *     card can't instantly price a section the customer checked.
 *
 * The CTA navigates to the routed RFQ creator page (/rfq/new) in STANDALONE
 * mode (replacing the old in-banner modal). The page reads the lane / mode /
 * transaction type straight from Redux, so the banner only needs to route.
 */
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Button } from 'antd';
import { isEmpty } from '../../util/isEmpty';
import { isLoggedInUser } from '../../util/isLoggedInUser';

const RfqResultsBanner = ({ secondaryColor, seaFCLJSON, seaLCLJSON, airJSON, userDetails }) => {
  const history = useHistory();

  // RFQ is a logged-in-only feature for now (decision #75 c11): guests sign up
  // first, so they never see the standalone "Request RFQ" entry point.
  if (!isLoggedInUser(userDetails)) return null;

  // Defensive: render nothing until there are results to sit above.
  if (isEmpty(seaFCLJSON) && isEmpty(seaLCLJSON) && isEmpty(airJSON)) return null;

  const accent = secondaryColor || '#f5222d';

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        flexWrap: 'wrap',
        padding: '10px 14px',
        marginBottom: 12,
        border: '1px solid #eee',
        borderLeft: `3px solid ${accent}`,
        borderRadius: 6,
        background: '#fff',
      }}
    >
      <div style={{ flex: '1 1 320px', fontSize: 13, color: '#555' }}>
        No competitive rate for your target ETD, or need door-to-door?{' '}
        <strong>Request an RFQ</strong> to get competitive quotes for this lane.
      </div>
      <Button
        onClick={() => history.push('/rfq/new', { source: 'standalone' })}
        style={{
          height: 'auto',
          padding: '6px 18px',
          border: 'none',
          color: '#fff',
          background: accent,
          fontSize: '14px',
          fontWeight: 600,
          borderRadius: '5px',
        }}
      >
        <span role="img" aria-label="lightning">⚡</span> Request RFQ
      </Button>
    </div>
  );
};

RfqResultsBanner.propTypes = {
  secondaryColor: PropTypes.string,
  seaFCLJSON: PropTypes.object,
  seaLCLJSON: PropTypes.object,
  airJSON: PropTypes.object,
  userDetails: PropTypes.object,
};

const mapStateToProps = (state) => ({
  secondaryColor: state.code_config.secondaryColor,
  seaFCLJSON: state.route.seaFCLJSON,
  seaLCLJSON: state.route.seaLCLJSON,
  airJSON: state.route.airJSON,
  userDetails: state.route.USER_DETAILS,
});

export default connect(mapStateToProps)(RfqResultsBanner);
